/**
 * Apollo cache helpers for note share/revoke mutations
 *
 * Encapsulates the `cache.modify` logic used to keep `listNoteShares`
 * in sync after `shareNote`/`revokeNoteShare` mutations without a network
 * round-trip. The store field name parsing handles the variable-keyed
 * cache slot (`listNoteShares({"noteId":"..."})`) so only the affected
 * note's cache entry is mutated (SE-4289).
 */

import type { ApolloCache, Reference } from "@apollo/client";

import { type NotesNoteShareFragment, UserNoteRole } from "@/generated/graphql";

/** A NoteShare entry as written into the Apollo cache */
export type CachedNoteShare = NotesNoteShareFragment;

/** Cognito attribute shape used inside the optimistic NoteShare entry */
type CognitoAttribute = {
  __typename?: "CognitoUserAttribute";
  Name: string;
  Value: string;
};

/**
 * Splits a display name into Cognito given_name + family_name attributes.
 * Used to build a synthetic NoteShare entry that ShareRecipientsList can
 * render with the correct display name without a network round-trip.
 * @param displayName - Display name to split (falls back to "User")
 * @returns Cognito user attributes
 */
const buildCognitoAttributes = (displayName: string): CognitoAttribute[] => {
  const trimmed = displayName.trim();
  const parts = trimmed.split(/\s+/);
  const firstName = parts[0] ?? "";
  const lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";
  return [
    {
      __typename: "CognitoUserAttribute",
      Name: "given_name",
      Value: firstName,
    },
    {
      __typename: "CognitoUserAttribute",
      Name: "family_name",
      Value: lastName,
    },
  ];
};

/**
 * Builds an optimistic NoteShare entry for cache writes.
 * The synthetic entry uses the targetUserId as both the share's user id
 * and as the basis for display name resolution if the user data is not
 * available from the org users list.
 * @param targetUserId - User ID being granted access
 * @param displayName - Display name from useShareModal's allOrgUsers
 * @returns A complete NotesNoteShareFragment shape suitable for cache writes
 */
export const buildOptimisticNoteShare = (
  targetUserId: string,
  displayName?: string
): CachedNoteShare => {
  // Use one fallback value for both `Username` and the Cognito attributes so
  // every consumer (Username-based or Attributes-based name resolution) shows
  // the same recipient label until the real response lands.
  // Treat empty / whitespace-only displayName the same as absent so the
  // optimistic entry always shows a meaningful label.
  const fallbackDisplayName = displayName?.trim()
    ? displayName.trim()
    : targetUserId;
  return {
    __typename: "NoteShare",
    userId: targetUserId,
    role: UserNoteRole.Viewer,
    createdAt: new Date().toISOString(),
    user: {
      __typename: "User",
      id: targetUserId,
      cognitoUser: {
        __typename: "CognitoUser",
        Username: fallbackDisplayName,
        Attributes: buildCognitoAttributes(fallbackDisplayName),
        UserAttributes: null,
      },
    },
  };
};

/**
 * Returns true if the given storeFieldName is the `listNoteShares` slot
 * for the supplied noteId. Apollo serializes the args into the field name
 * as `listNoteShares({"noteId":"..."})`, so a substring match against the
 * JSON-encoded variable is sufficient.
 * @param storeFieldName - Apollo store field name
 * @param noteId - Note ID to match
 * @returns Whether the field name targets the supplied noteId
 */
const matchesNoteId = (storeFieldName: string, noteId: string): boolean =>
  storeFieldName.includes(`"noteId":"${noteId}"`);

/** Inline NoteShare or normalized Reference, as found in cache */
type CachedShareEntry = CachedNoteShare | Reference;

/** Apollo modifier value for the listNoteShares field — list or single ref */
type ListNoteSharesValue = readonly CachedShareEntry[] | Reference;

/**
 * Narrow ListNoteSharesValue to a list, treating any non-list as empty
 * @param value - Apollo modifier value (list of shares or single Reference)
 * @returns A readonly array of cached share entries
 */
const asList = (
  value: ListNoteSharesValue | undefined
): readonly CachedShareEntry[] => (Array.isArray(value) ? value : []);

/**
 * Reads the userId field from a cached share, handling both inline objects
 * and normalized references via Apollo's readField helper.
 * @param share - Cached share entry (inline NoteShare or normalized Reference)
 * @param readField - Apollo helper that reads a field across both shapes
 * @returns The share's userId, or undefined if unreadable
 */
const readUserId = (
  share: CachedShareEntry,
  readField: <T>(name: string, from?: CachedShareEntry) => T | undefined
): string | undefined => readField<string>("userId", share);

/**
 * Cache modifier that appends a new share entry to listNoteShares for the
 * specified noteId. No-op if the user already has a share entry (avoids
 * duplicates when an optimistic write races a real response).
 * @param cache - Apollo cache instance
 * @param noteId - Note whose listNoteShares slot to update
 * @param newShare - Optimistic NoteShare to append
 */
export const modifyListNoteSharesAdd = <T>(
  cache: ApolloCache<T>,
  noteId: string,
  newShare: CachedNoteShare
): void => {
  cache.modify({
    fields: {
      listNoteShares(existing: ListNoteSharesValue, details) {
        if (!matchesNoteId(details.storeFieldName, noteId)) return existing;
        const list = asList(existing);
        const alreadyPresent = list.some(
          share => readUserId(share, details.readField) === newShare.userId
        );
        if (alreadyPresent) return list;
        return [...list, newShare];
      },
    },
  });
};

/**
 * Cache modifier that removes a share entry from listNoteShares for the
 * specified noteId, matched by targetUserId.
 * @param cache - Apollo cache instance
 * @param noteId - Note whose listNoteShares slot to update
 * @param targetUserId - User ID whose share should be removed
 */
export const modifyListNoteSharesRemove = <T>(
  cache: ApolloCache<T>,
  noteId: string,
  targetUserId: string
): void => {
  cache.modify({
    fields: {
      listNoteShares(existing: ListNoteSharesValue, details) {
        if (!matchesNoteId(details.storeFieldName, noteId)) return existing;
        const list = asList(existing);
        return list.filter(
          share => readUserId(share, details.readField) !== targetUserId
        );
      },
    },
  });
};
